/**
 * PanelLayout — persistent left-column shell for the user panel
 * (DASHBOARD-SIDEMENU-PROFILES-1-FIX2 · Feb 2026).
 *
 * Wraps `PublicLayout` and mounts `DashboardSideMenu` as a fixed,
 * always-visible navigation column to the left of the page content.
 * Used by every `/app/*` user route reachable from the side menu so the
 * menu persists when the user switches sections / submenus.
 *
 * Layout
 *   • Desktop (lg+) — two columns: `240px` menu | `1fr` content,
 *     `gap-4` (tighter than the previous `gap-6` to satisfy the
 *     "menos separación" requirement).
 *   • Mobile — menu stacks above content.
 *
 * Frontend-only · no backend calls.
 */
import React, { useEffect } from "react";
import { useLocation } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import DashboardSideMenu from "@/components/dashboard/DashboardSideMenu";

export default function PanelLayout({ children }) {
  // FINAL-DEMO-UX-I18N-EQUIPMENT-QA-1: when the user clicks a sidebar
  // entry (route change) or jumps to a Mi perfil hash, reset window
  // scroll so the page header + H1 are always visible. Avoids landing
  // mid-page on Settings sub-sections.
  const location = useLocation();
  useEffect(() => {
    // Use rAF so the new content has mounted before we scroll.
    const raf = requestAnimationFrame(() => {
      window.scrollTo({ top: 0, left: 0, behavior: "instant" in window ? "instant" : "auto" });
    });
    return () => cancelAnimationFrame(raf);
  }, [location.pathname, location.hash]);

  return (
    <PublicLayout>
      <div
        className="tsw-section pt-6 pb-12"
        data-testid="panel-layout-root"
      >
        <div className="lg:grid lg:grid-cols-[240px_minmax(0,1fr)] lg:gap-4">
          <DashboardSideMenu />
          <div
            className="mt-4 lg:mt-0 min-w-0"
            data-testid="panel-layout-content"
          >
            {children}
          </div>
        </div>
      </div>
    </PublicLayout>
  );
}
